import { useEffect, useState } from "react";
import { ArrowRight, BadgeCheck, MapPin, Menu, MessageCircle, Phone, X } from "lucide-react";
import { NavLink } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Logo } from "./Logo";
import { ButtonLink } from "@/components/ui/Button";
import { companyInfo } from "@/data/siteData";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/packages", label: "Packages" },
  { to: "/destinations", label: "Destinations" },
  { to: "/services", label: "Services" },
  { to: "/travel-guides", label: "Travel Guides" },
  { to: "/about", label: "About" },
];

export function AnnouncementBar() {
  return (
    <div className="announcement-bar">
      <div className="container announcement-bar__inner">
        <span><BadgeCheck size={14} /> DOT-accredited travel agency</span>
        <span className="announcement-bar__desktop">
          <MapPin size={14} /> Cauayan City, Isabela
        </span>
        <a href={companyInfo.messengerUrl} target="_blank" rel="noreferrer">
          Chat with ACES <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <AnnouncementBar />
      <header className={`site-header${scrolled ? " site-header--scrolled" : ""}`}>
        <div className="container site-header__inner">
          <Logo />
          <nav className="desktop-nav" aria-label="Primary navigation">
            {navItems.map(({ to, label, end }) => (
              <NavLink key={to} to={to} end={end}>
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="site-header__actions">
            <a
              className="header-contact"
              href={`tel:${companyInfo.phone.replace(/[^\d+]/g, "")}`}
              aria-label="Call ACES Travel"
            >
              <Phone size={18} />
            </a>
            <ButtonLink
              to="/plan-your-trip"
              size="sm"
              icon={<ArrowRight size={16} />}
              className="desktop-cta"
            >
              Plan my trip
            </ButtonLink>
            <button
              className="mobile-menu-button"
              aria-label="Open navigation"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <Menu />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-nav"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button
              className="mobile-nav__backdrop"
              aria-label="Close navigation"
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              className="mobile-nav__panel"
              initial={reduceMotion ? false : { x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              aria-label="Mobile navigation"
            >
              <div className="mobile-nav__top">
                <Logo />
                <button aria-label="Close navigation" onClick={() => setMenuOpen(false)}>
                  <X />
                </button>
              </div>
              <nav>
                {navItems.map(({ to, label, end }, index) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={() => setMenuOpen(false)}
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    {label}
                    <ArrowRight size={17} />
                  </NavLink>
                ))}
              </nav>
              <div className="mobile-nav__footer">
                <ButtonLink
                  to="/plan-your-trip"
                  size="lg"
                  icon={<ArrowRight size={18} />}
                  onClick={() => setMenuOpen(false)}
                >
                  Plan with ACES
                </ButtonLink>
                <a href={companyInfo.messengerUrl} target="_blank" rel="noreferrer">
                  <MessageCircle size={17} /> Chat with our team
                </a>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
